import { useTranslations } from '@vocab/react';
import { RadioGroup, RadioItem } from 'braid-design-system';
import React, { type FormEvent, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { EVENT_NAME_INTERACTION } from 'src/app/analytics';
import getTrackingAdapter from 'src/app/dataAnalytics';
import { WORK_TYPE_PRESSED } from 'src/app/dataAnalytics/schema/constants';
import {
  FEATURE_DATA_ANALYTICS,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { actionUpdateWorkType } from 'src/app/state/modules/formData/workType';
import { actionUpdateCajaWorkType } from 'src/app/state/modules/tealium/cajaWorkType';
import { useWorkTypeTaxonomy } from 'src/app/taxonomies/useWorkTypeTaxonomy';
import { sendLinkEvent } from 'src/app/utils/analytics';
import type AppState from 'src/types/appState';
import type { Option } from 'src/types/formFields';

import translations from './.vocab';

const ANALYTICS_LINK_NAME = 'ecaja-work-type-selected';

export const WorkType = () => {
  const { workType, tealium } = useSelector(
    (state: AppState) => ({
      workType: state.formData.workType,
      tealium: state.tealium,
    }),
    shallowEqual,
  );

  const dispatch = useDispatch();

  const [inputValue, setInputValue] = useState<string>(workType);

  const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);

  const { t } = useTranslations(translations);

  const { workTypes }: { workTypes: Option[] } = useWorkTypeTaxonomy();

  const handleOnChange = (event: FormEvent<HTMLInputElement>): void => {
    const { value } = event.currentTarget;

    setInputValue(value);

    dispatch(actionUpdateWorkType(value));
    dispatch(actionUpdateCajaWorkType(value));

    if (isPageAnalyticsEnabled) {
      const eventData = {
        eventName: WORK_TYPE_PRESSED,
        objectInteraction: `work-type-${value}`,
        jobWorkType: value,
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    } else {
      sendLinkEvent(tealium, {
        linkName: ANALYTICS_LINK_NAME,
        eventName: [EVENT_NAME_INTERACTION, ANALYTICS_LINK_NAME],
        objectInteraction: `work-type-${value}`,
        cajaWorkType: value,
      });
    }
  };

  return (
    <RadioGroup
      id="WorkTypeRadioButtonGroup"
      label={t('work-type-label')}
      onChange={handleOnChange}
      value={inputValue}
    >
      {workTypes.map(({ text, value }: Option, index: number) => (
        <RadioItem key={`work-type-${index}`} label={text} value={value} />
      ))}
    </RadioGroup>
  );
};
